/**
 * Station koppeln — der Bildschirm am neuen Gerät.
 *
 * Läuft vor jeder Anmeldung, darum die mittige Karte im Leitstand-Stil ohne
 * Rahmen und ohne Navigation. Die Maße bleiben großzügig: Diese Seite steht
 * in der Halle (siehe docs/BETRIEB-ELIS.md, "Stationen: Die Größen sind
 * Absicht").
 *
 * BEWUSST KARG (Andreas, 18.08.2026): Die Seite ist offen erreichbar. Sie
 * zeigt nur den QR-Code und die Kurzform — keine Anleitung, wo oder wie
 * freigegeben wird, und keinen kopierbaren Link. Wer hier "hilfreicher" wird,
 * verschenkt genau die Information, die den Kopplungs-Code schützt.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Monitor, RefreshCw, Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';
import { initPairing, claimPairing, ApiException } from '../api/client';
import QRCode from 'qrcode';
import { sichereRuecksprungAdresse } from '../lib/weiterleitung';

type PairingState = 'loading' | 'ready' | 'polling' | 'paired' | 'expired' | 'error';

export default function PairPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // returnTo aus URL Query Parameter
  const returnTo = sichereRuecksprungAdresse(searchParams.get('returnTo'));

  const [state, setState] = useState<PairingState>('loading');
  const [token, setToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stationType, setStationType] = useState<string | null>(null);

  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const expiryCheckRef = useRef<NodeJS.Timeout | null>(null);

  // Netflix-Muster (Review S1): Der QR trägt nur den Code selbst — kein Link,
  // der irgendwo geöffnet, kopiert oder geloggt werden könnte. Gescannt wird
  // im angemeldeten Leitstand unter Einrichten → Gerät koppeln.
  const getPairingUrl = useCallback((t: string) => t, []);

  // QR-Code generieren
  const generateQRCode = useCallback(async (url: string) => {
    try {
      // Feste Werte mit Absicht: Ein QR-Code muss dunkel auf hell stehen,
      // sonst liest ihn keine Kamera. Er folgt deshalb keinem Farbschema.
      const dataUrl = await QRCode.toDataURL(url, {
        width: 256,
        margin: 2,
        color: {
          dark: '#1e293b',
          light: '#ffffff',
        },
      });
      setQrDataUrl(dataUrl);
    } catch (err) {
      console.error('QR-Code Generierung fehlgeschlagen:', err);
    }
  }, []);

  // Pairing Session initialisieren
  const initSession = useCallback(async () => {
    setState('loading');
    setError(null);
    setToken(null);
    setQrDataUrl(null);

    // Polling stoppen falls aktiv
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    try {
      const deviceHint = navigator.userAgent;
      const result = await initPairing(deviceHint);

      setToken(result.token);
      setExpiresAt(new Date(result.expiresAt));

      // QR-Code generieren
      const url = getPairingUrl(result.token);
      await generateQRCode(url);

      setState('ready');
      startPolling(result.token);
      startExpiryCheck(new Date(result.expiresAt));
    } catch (err) {
      if (err instanceof ApiException) {
        if (err.data.code === 'RATE_LIMITED') {
          setError(
            `Zu viele Anfragen. Bitte ${err.data.retryAfter || 60} Sekunden warten, dann noch einmal versuchen.`,
          );
        } else {
          setError(
            err.data.error ||
              'Der Code lässt sich gerade nicht erzeugen. Bitte noch einmal versuchen.',
          );
        }
      } else {
        setError('Keine Verbindung zum Server. Bitte noch einmal versuchen.');
      }
      setState('error');
    }
  }, [getPairingUrl, generateQRCode]);

  // Polling starten
  const startPolling = useCallback(
    (t: string) => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }

      const poll = async () => {
        try {
          const result = await claimPairing(t);

          if (result.status === 'paired') {
            // Polling stoppen
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
            if (expiryCheckRef.current) {
              clearInterval(expiryCheckRef.current);
              expiryCheckRef.current = null;
            }

            setStationType(result.stationType || null);
            setState('paired');

            // Nach kurzer Verzögerung redirect zu returnTo
            setTimeout(() => {
              navigate(returnTo);
            }, 2000);
          }
        } catch (err) {
          if (err instanceof ApiException) {
            if (err.data.code === 'PAIRING_EXPIRED' || err.data.code === 'PAIRING_NOT_FOUND') {
              if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
              }
              setState('expired');
            } else if (err.data.code === 'RATE_LIMITED') {
              // Bei Rate Limit kurz pausieren, aber weiter versuchen
              console.warn('Claim rate limited, waiting...');
            }
          }
        }
      };

      // Sofort einmal prüfen, dann alle 2 Sekunden
      poll();
      pollingRef.current = setInterval(poll, 2000);
    },
    [navigate, returnTo],
  );

  // Expiry Check starten
  const startExpiryCheck = useCallback((expires: Date) => {
    if (expiryCheckRef.current) {
      clearInterval(expiryCheckRef.current);
    }

    expiryCheckRef.current = setInterval(() => {
      if (new Date() >= expires) {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        if (expiryCheckRef.current) {
          clearInterval(expiryCheckRef.current);
          expiryCheckRef.current = null;
        }
        setState('expired');
      }
    }, 1000);
  }, []);

  // Beim Laden initialisieren
  useEffect(() => {
    initSession();

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      if (expiryCheckRef.current) {
        clearInterval(expiryCheckRef.current);
      }
    };
  }, [initSession]);

  // Verbleibende Zeit berechnen
  const getRemainingTime = () => {
    if (!expiresAt) return null;
    const remaining = Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000));
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Token-Kurzform (erste 8 Zeichen des Tokens) - nur zur visuellen Identifikation
  const tokenShort = token ? token.substring(0, 8).toUpperCase() : null;

  return (
    <div className="min-h-screen bg-grund text-tinte flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <p className="mb-5 text-center font-zahl text-[15px] font-semibold tracking-[0.06em] text-tinte-2">
          wash<b className="font-semibold text-akzent">·</b>ops
        </p>

        <div className="rounded-leitstand border border-linie bg-flaeche p-8">
          {/* Kopf */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-flaeche-2 rounded-full flex items-center justify-center mx-auto mb-4">
              <Monitor className="w-8 h-8 text-tinte-2" />
            </div>
            <h1 className="text-2xl font-bold text-tinte">Station koppeln</h1>
            <p className="text-tinte-2 mt-1">Dieses Gerät als Station registrieren</p>
          </div>

          {/* Code wird geholt */}
          {state === 'loading' && (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 animate-spin mx-auto text-tinte-3" />
              <p className="text-tinte-2 mt-4">Code wird vorbereitet...</p>
            </div>
          )}

          {/* Code steht — QR und Kurzform, sonst nichts */}
          {(state === 'ready' || state === 'polling') && qrDataUrl && (
            <div className="space-y-6">
              {/* Der QR bringt seinen hellen Grund selbst mit; der Rahmen
                  setzt ihn nur ab. */}
              <div className="flex justify-center">
                <img
                  src={qrDataUrl}
                  alt="QR-Code zum Koppeln"
                  className="w-48 h-48 rounded-lg border-2 border-linie-2"
                />
              </div>

              {/* Bewusst keine Anleitung: Die Seite ist offen erreichbar und
                  verrät nicht, wo oder wie freigegeben wird (Andreas, 18.08.). */}
              {/* Kurzform des Codes — nur zum Wiedererkennen */}
              <div className="rounded-md border border-linie-2 bg-flaeche-2 p-3 text-center">
                <p className="text-xs text-tinte-3 mb-1">Code</p>
                <p className="text-lg font-zahl font-bold text-tinte tracking-widest">
                  {tokenShort}
                </p>
              </div>

              {/* Restlaufzeit */}
              <div className="flex items-center justify-center gap-2 text-sm text-tinte-2">
                <Clock className="w-4 h-4" />
                <span>Gültig noch {getRemainingTime()}</span>
              </div>

              {/* Zustand */}
              <div className="flex items-center justify-center gap-2 text-akzent">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Warten auf Freigabe...</span>
              </div>
            </div>
          )}

          {/* Gekoppelt */}
          {state === 'paired' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-flaeche-2 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-lauf" />
              </div>
              <h2 className="text-xl font-semibold text-lauf mb-2">Station gekoppelt</h2>
              <p className="text-tinte-2">
                {stationType === 'WSA' && 'WSA-Erfassung aktiv'}
                {stationType === 'VORARBEITER' && 'Vorarbeiter-Station aktiv'}
                {stationType === 'ADMIN' && 'Admin-Station aktiv'}
                {!stationType && 'Station erfolgreich registriert'}
              </p>
              <p className="text-sm text-tinte-3 mt-4">Weiter zur Anmeldung...</p>
            </div>
          )}

          {/* Zeit abgelaufen */}
          {state === 'expired' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-flaeche-2 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-stand" />
              </div>
              <h2 className="text-xl font-semibold text-stand mb-2">Code abgelaufen</h2>
              <p className="text-tinte-2 mb-6">Bitte einen neuen Code erzeugen.</p>
              <button
                onClick={initSession}
                className="rounded-md border border-akzent bg-akzent px-6 py-2 font-medium text-akzent-satz transition-colors flex items-center gap-2 mx-auto"
              >
                <RefreshCw className="w-4 h-4" />
                Neuen Code erzeugen
              </button>
            </div>
          )}

          {/* Fehler */}
          {state === 'error' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-flaeche-2 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-stoer" />
              </div>
              <h2 className="text-xl font-semibold text-stoer mb-2">Hat nicht geklappt</h2>
              <p className="text-tinte-2 mb-6">{error || 'Bitte noch einmal versuchen.'}</p>
              <button
                onClick={initSession}
                className="rounded-md border border-akzent bg-akzent px-6 py-2 font-medium text-akzent-satz transition-colors flex items-center gap-2 mx-auto"
              >
                <RefreshCw className="w-4 h-4" />
                Erneut versuchen
              </button>
            </div>
          )}

          {/* Zurück */}
          <div className="mt-8 text-center">
            <a href="/login" className="text-sm text-tinte-3 transition-colors hover:text-tinte-2">
              Zurück zur Anmeldung
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
